/**
 * AdminSeatsTab — platform-wide seat economics for the Owner-Admin panel.
 *
 * Pulls /api/admin/seats-overview and renders:
 *   • Four KPI cards (workspaces, total seats used, add-on MRR, over-cap count)
 *   • Per-plan breakdown grid
 *   • Top-25 workspaces leaderboard by seat usage
 *   • Over-cap workspaces table (the upsell-outreach list)
 *
 * Every workspace row links to /admin/customers/{ws_id} for drill-in.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Users, Building2, DollarSign, AlertTriangle, ChevronRight,
  Loader2, RefreshCw, Crown, Infinity as InfinityIcon, Layers,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);

function KPI({ icon: Icon, label, value, hint, accentClass = '' }) {
  return (
    <Card data-testid={`seats-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
            {label}
          </div>
          <div className={`p-1.5 rounded-md ${accentClass || 'bg-muted'}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <div className="text-2xl font-semibold mt-2">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export default function AdminSeatsTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/seats-overview`, {
        credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to load seats overview.');
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="seats-tab-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-3">
          <AlertTriangle className="w-8 h-8 mx-auto text-rose-500" />
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button onClick={load} variant="outline" size="sm">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { kpis, per_plan, top_workspaces, over_cap_workspaces, addon_seat_price_dollars } = data;
  const addonPrice = addon_seat_price_dollars || 5;

  return (
    <div className="space-y-6" data-testid="admin-seats-tab">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          icon={Building2}
          label="Workspaces"
          value={fmtNum(kpis.total_workspaces)}
          hint="All customer accounts"
          accentClass="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
        />
        <KPI
          icon={Users}
          label="Seats used"
          value={fmtNum(kpis.total_billable_seats_used)}
          hint={`${fmtNum(kpis.total_members)} total members (incl. owners)`}
          accentClass="bg-violet-100 dark:bg-violet-950 text-violet-700 dark:text-violet-300"
        />
        <KPI
          icon={DollarSign}
          label="Add-on seat MRR"
          value={fmtUSD(kpis.addon_mrr_dollars)}
          hint={`${fmtNum(kpis.total_addon_seats)} seats × $${addonPrice}/mo`}
          accentClass="bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300"
        />
        <KPI
          icon={AlertTriangle}
          label="Over cap"
          value={fmtNum(kpis.over_cap_workspaces)}
          hint="Outreach opportunities"
          accentClass="bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300"
        />
      </div>

      {/* Per-plan breakdown */}
      <Card data-testid="seats-per-plan-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="w-4 h-4 text-violet-600" />
            Per-plan breakdown
          </CardTitle>
          <CardDescription>How seats are distributed across your pricing tiers.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Workspaces</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead className="text-right">Seats used</TableHead>
                <TableHead className="text-right">Add-on seats</TableHead>
                <TableHead className="text-right">Add-on MRR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {per_plan.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                    No customer workspaces yet.
                  </TableCell>
                </TableRow>
              )}
              {per_plan.map((p) => (
                <TableRow key={p.plan_id} data-testid={`seats-plan-row-${p.plan_id}`}>
                  <TableCell>
                    <Badge variant="secondary" className="font-medium">{p.plan_label}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(p.workspaces)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(p.members)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtNum(p.billable_used)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(p.addon_seats)}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-300 font-medium">
                    {fmtUSD(p.addon_seats * addonPrice)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Over-cap workspaces (outreach list) */}
      {over_cap_workspaces.length > 0 && (
        <Card className="border-rose-200 dark:border-rose-900" data-testid="seats-over-cap-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              Over their seat cap ({over_cap_workspaces.length})
            </CardTitle>
            <CardDescription>
              These workspaces are using more seats than their plan allows. Reach out — upsell to a higher tier or sell add-on seats.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Used / Cap</TableHead>
                  <TableHead className="text-right">Over by</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {over_cap_workspaces.map((w) => (
                  <TableRow
                    key={w.workspace_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/owner-admin/customers/${w.workspace_id}`)}
                    data-testid={`seats-overcap-row-${w.workspace_id}`}
                  >
                    <TableCell className="font-medium">{w.name}</TableCell>
                    <TableCell><Badge variant="outline">{w.plan_label}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNum(w.billable_used)} / {w.is_unlimited ? '∞' : fmtNum(w.total_cap)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive" className="tabular-nums">+{w.over_cap_delta}</Badge>
                    </TableCell>
                    <TableCell><ChevronRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Top-25 leaderboard */}
      <Card data-testid="seats-top-workspaces-card">
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Crown className="w-4 h-4 text-amber-500" />
              Top workspaces by seat usage
            </CardTitle>
            <CardDescription>The 25 workspaces with the most active seats.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="seats-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Workspace</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Seats used</TableHead>
                <TableHead className="text-right">Cap</TableHead>
                <TableHead className="text-right">Add-on</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top_workspaces.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    No workspaces yet.
                  </TableCell>
                </TableRow>
              )}
              {top_workspaces.map((w, i) => (
                <TableRow
                  key={w.workspace_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/owner-admin/customers/${w.workspace_id}`)}
                  data-testid={`seats-top-row-${i}`}
                >
                  <TableCell className="text-muted-foreground tabular-nums">{i + 1}</TableCell>
                  <TableCell className="font-medium">{w.name}</TableCell>
                  <TableCell><Badge variant="secondary">{w.plan_label}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtNum(w.billable_used)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {w.is_unlimited ? <InfinityIcon className="w-4 h-4 inline" /> : fmtNum(w.total_cap)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {w.addon_seats ? `+${w.addon_seats}` : '—'}
                  </TableCell>
                  <TableCell><ChevronRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
